import React, { useState } from 'react';
import PatientChart from '../management/PatientChart';
import DoutoOfferPanel from './DoutoOfferPanel';
import ProfileSteps from './ProfileSteps';
import { offerFor, offerHref } from '../../lib/segmentOffers';
import { useLocale } from '../LocaleProvider';

// Mesmos dados fictícios de exemplo usados na prévia de /pro — sem paciente real.
const EXEMPLO = [
  { id: 'ex1', patient_id: 'ex', score: 26, applied_at: '2025-03-12', reference_key: 'brucki', created_at: '2025-03-12' },
  { id: 'ex2', patient_id: 'ex', score: 25, applied_at: '2025-09-18', reference_key: 'brucki', created_at: '2025-09-18' },
  { id: 'ex3', patient_id: 'ex', score: 23, applied_at: '2026-03-20', reference_key: 'brucki', created_at: '2026-03-20' },
];

const STEPS = ['perfil', 'tour', 'oferta', 'douto'];

// Envia o passo do perfil ao servidor (que valida, registra e decide se qualifica). Nunca trava a navegação.
async function sendProfile(evento, payload) {
  try {
    const res = await fetch('/api/auth/onboarding-profile', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ evento, ...payload }),
    });
    return await res.json().catch(() => null);
  } catch { return null; }
}


async function markOnboardingComplete(locale) {
  try {
    await fetch('/api/auth/onboarding-complete', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ idioma_origem: locale }) });
  } catch { /* a próxima visita tenta de novo; nunca trava a navegação */ }
}

function ProgressDashes({ step }) {
  return (
    <div className="flex gap-1.5" aria-hidden="true">
      {STEPS.map((_, i) => (
        <span key={i} className="h-0.5 w-6 rounded-full" style={{ background: i <= step ? 'var(--forest-rule)' : 'var(--forest-line)' }} />
      ))}
    </div>
  );
}

export default function OnboardingFlow({ name, email, onDone }) {
  const { locale } = useLocale();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(null);
  const [seed] = useState(() => `${Date.now()}-${Math.random()}`);
  const stepName = STEPS[step];

  async function finish() {
    await markOnboardingComplete(locale);
    onDone?.();
  }

  async function completeProfile(data) {
    setProfile(data);
    setStep(1);
    sendProfile('completar', { ...data, idioma_origem: locale });
  }

  const offer = profile ? offerFor(profile.answers) : null;
  const spanishOffers = {
    kit_plantonista: { titulo: 'Muestra gratis del Kit do Plantonista', texto: 'Consulta una muestra del material brasileño sobre conducta y prescripción. El contenido está en portugués de Brasil.', cta: 'Ver muestra gratis' },
    kit_receitas: { titulo: 'Muestra gratis del Kit, con recetas modelo', texto: 'Consulta una muestra de la guía brasileña de prescripción y sus recetas modelo. El contenido está en portugués de Brasil.', cta: 'Ver muestra gratis' },
    kit_residente: { titulo: 'Muestra gratis del Kit do Plantonista', texto: 'Material brasileño de conducta y prescripción para consultar durante la guardia. El contenido está en portugués de Brasil.', cta: 'Ver muestra' },
    kit_estudante: { titulo: 'Muestra gratis del Kit', texto: 'Consulta una muestra brasileña de conducta y prescripción. El contenido está en portugués de Brasil.', cta: 'Ver muestra gratis' },
    amostra_generica: { titulo: 'Muestra gratis', texto: 'Consulta una muestra de los materiales de consulta rápida de EI DOC, en portugués de Brasil.', cta: 'Ver muestra gratis' },
  };
  const localizedOffer = locale === 'es' && offer ? (spanishOffers[offer.id] || offer) : offer;
  const titles = {
    perfil: locale === 'es' ? ['Cuenta creada', 'Cuéntanos un poco sobre ti'] : ['Conta criada', 'Conte um pouco sobre você'],
    tour: locale === 'es' ? ['Cuenta creada', 'Bienvenido al historial de seguimiento'] : ['Conta criada', 'Bem-vindo ao histórico do paciente'],
    oferta: locale === 'es' ? ['Para ti', localizedOffer?.titulo || ''] : ['Para você', offer?.titulo || ''],
    douto: locale === 'es' ? ['Un regalo de bienvenida', 'Consulta tus dudas clínicas con Douto'] : ['Um presente de boas-vindas', 'Leve suas dúvidas clínicas ao Douto'],
  };

  return (
    <div className="fixed inset-0 z-[90] flex min-h-dvh items-start justify-center overflow-y-auto bg-[rgba(36,64,47,0.55)] p-4 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
      <div className="relative my-auto max-h-[calc(100dvh-2rem)] w-full max-w-xl overflow-hidden rounded-[var(--radius-block)] bg-[var(--surface)] sm:max-h-[calc(100dvh-3rem)]">
        <p className="sr-only" role="status">{locale === 'es' ? `Paso ${step + 1} de ${STEPS.length}` : `Passo ${step + 1} de ${STEPS.length}`}</p>
        <header className="band-forest px-6 py-6 sm:px-8">
          <ProgressDashes step={step} />
          <span aria-hidden="true" className="eyebrow-rule mt-3 mb-2" />
          <p className="eyebrow text-sm text-[var(--on-forest-2)]">{titles[stepName][0]}</p>
          <h2 id="onboarding-title" className="mt-1 text-2xl">{titles[stepName][1]}</h2>
        </header>

        <div className="overflow-y-auto p-6 sm:p-8" style={{ maxHeight: 'calc(100dvh - 14rem)' }}>
          {stepName === 'perfil' && (
            <ProfileSteps seed={seed} onComplete={completeProfile} onSkip={() => setStep(1)} />
          )}

          {stepName === 'tour' && (
            <>
              <p className="text-sm leading-relaxed text-[var(--text-muted)]">{locale === 'es' ? 'La interfaz de seguimiento está en español, pero el instrumento y sus instrucciones se mantienen en portugués brasileño (PT-BR); esta pantalla no es una traducción validada del MMSE. El panel permite guardar y comparar puntuaciones.' : 'A calculadora continua igual. A diferença é que agora cada MEEM pode ser guardado na ficha do paciente e comparado com os anteriores, como no exemplo abaixo.'}</p>
              <div className="chart-box mt-5 p-4">
                <p className="eyebrow text-xs">{locale === 'es' ? 'Ejemplo con datos ficticios: tres evaluaciones en un año' : 'Exemplo com dados fictícios — três avaliações em um ano'}</p>
                <div className="mt-3"><PatientChart assessments={EXEMPLO} /></div>
              </div>
              <button type="button" onClick={() => setStep(profile && profile.answers.amostra === 'sim' ? 2 : 3)} className="btn-primary mt-6 w-full">{locale === 'es' ? 'Ver mi regalo' : 'Ver meu presente'} <span aria-hidden="true">→</span></button>
            </>
          )}

          {stepName === 'oferta' && offer && (
            <>
              <p className="text-sm leading-relaxed text-[var(--text-muted)]">{localizedOffer.texto}</p>
              <a
                href={offerHref(offer, profile.answers.segmento)}
                target="_blank" rel="noopener noreferrer"
                onClick={() => { sendProfile('clique_oferta', profile); setStep(3); }}
                className="btn-primary mt-6 block w-full text-center"
              >{localizedOffer.cta} <span aria-hidden="true">→</span></a>
              <button type="button" onClick={() => setStep(3)} className="mt-5 block w-full text-right text-sm text-[var(--text-muted)] underline">{locale === 'es' ? 'Ahora no' : 'Agora não'}</button>
            </>
          )}

          {stepName === 'douto' && (
            <>
              <p className="text-sm leading-relaxed text-[var(--text-muted)] mb-5">{locale === 'es' ? 'Como agradecimiento por usar MiniMental, puedes empezar a usar Douto con descuento y un mes de cortesía.' : 'Como agradecimento por usar o MiniMental, você começa no Douto com desconto e um mês de cortesia.'}</p>
              <DoutoOfferPanel context="onboarding" name={name} email={email} onLinkClick={() => finish()} />
              <button type="button" onClick={finish} className="mt-5 block w-full text-right text-sm text-[var(--text-muted)] underline">{locale === 'es' ? 'Omitir por ahora' : 'Pular por agora'}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
